import { Heading } from 'element-tiptap'
import { Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'
import { toggleBlockType } from 'tiptap-commands'

function isHeading (node) {
  return node && node.type.name === 'heading'
}

function findHeadings (doc) {
  const headings = []
  doc.descendants((node, pos) => {
    if (isHeading(node)) {
      headings.push({ node, pos, level: node.attrs.level })
    }
  })
  return headings
}

export default class CollapsibleHeading extends Heading {
  get name () {
    return 'heading'
  }

  get defaultOptions () {
    return {
      levels: [1, 2, 3, 4, 5, 6],
      collapsible: true,
      collapsed: false
    }
  }

  get schema () {
    return {
      attrs: {
        level: {
          default: 1
        },
        collapsed: {
          default: false
        }
      },
      content: 'inline*',
      group: 'block',
      defining: true,
      draggable: false,
      parseDOM: this.options.levels.map(level => ({
        tag: `h${level}`,
        attrs: { level, collapsed: false }
      })),
      toDOM: node => {
        const attrs = {
          'data-level': node.attrs.level,
          class: `collapsible-heading ${node.attrs.collapsed ? 'collapsed' : ''}`,
          'data-collapsible': 'true',
          'data-collapsed': node.attrs.collapsed ? 'true' : 'false'
        }

        return [`h${node.attrs.level}`, attrs, 0]
      }
    }
  }

  commands ({ type, schema }) {
    return {
      heading: attrs => toggleBlockType(type, schema.nodes.paragraph, attrs),
      collapseAll: () => (state, dispatch) => {
        console.log('[Log] collapseAll: 命令被调用')
        const { tr } = state
        const headings = findHeadings(state.doc)
        let modified = false
        headings.forEach(({ node, pos }) => {
          console.log(`[Log] collapseAll: 找到标题 at ${pos}, 折叠状态: ${node.attrs.collapsed}`)
          if (node.attrs.collapsed !== true) {
            tr.setNodeMarkup(pos, null, { ...node.attrs, collapsed: true })
            modified = true
          }
        })
        if (modified && dispatch) {
          console.log('[Log] collapseAll: 检测到修改，分发事务')
          dispatch(tr)
          return true
        }
        console.log('[Log] collapseAll: 未检测到修改，不分发事务')
        return false
      },
      expandAll: () => (state, dispatch) => {
        console.log('[Log] expandAll: 命令被调用')
        const { tr } = state
        const headings = findHeadings(state.doc)
        let modified = false
        headings.forEach(({ node, pos }) => {
          console.log(`[Log] expandAll: 找到标题 at ${pos}, 折叠状态: ${node.attrs.collapsed}`)
          if (node.attrs.collapsed) {
            tr.setNodeMarkup(pos, null, { ...node.attrs, collapsed: false })
            modified = true
          }
        })
        if (modified && dispatch) {
          console.log('[Log] expandAll: 检测到修改，分发事务')
          dispatch(tr)
          return true
        }
        console.log('[Log] expandAll: 未检测到修改，不分发事务')
        return false
      }
    }
  }

  get plugins () {
    return [
      new Plugin({
        key: new PluginKey('collapsible_heading_click'),
        props: {
          handleClick: (view, pos, event) => {
            const { state } = view;
            const { target } = event;

            if (!target) return false;

            // First, quickly check if the click was in the gutter area
            const headingDomNode = (target.nodeName.match(/^H[1-6]$/)) ? target : target.closest('h1, h2, h3, h4, h5, h6');
            if (!headingDomNode) return false;

            const rect = headingDomNode.getBoundingClientRect();
            if (event.clientX - rect.left > 25) {
                // Click was not in the gutter, so we don't handle it.
                return false;
            }
            
            // If it was in the gutter, now we find the correct ProseMirror node and position
            const resolvedPos = state.doc.resolve(pos);
            for (let i = resolvedPos.depth; i >= 0; i--) {
                const node = resolvedPos.node(i);
                if (isHeading(node)) {
                    // We found the heading node. Now get its start position.
                    const headingStartPos = resolvedPos.before(i);
                    
                    console.log(`%c[Log] handleClick: Found heading node to toggle:`, 'color: green; font-weight: bold;', {
                        nodeName: node.type.name,
                        level: node.attrs.level,
                        currentCollapsedState: node.attrs.collapsed,
                        pos: headingStartPos,
                    });

                    event.preventDefault();
                    event.stopPropagation();

                    const tr = state.tr.setNodeMarkup(headingStartPos, null, {
                        ...node.attrs,
                        collapsed: !node.attrs.collapsed,
                    });
                    
                    console.log('%c[Log] handleClick: 即将分发事务 (Dispatching transaction)', 'color: orange;', tr);
                    view.dispatch(tr);
                    console.log('%c[Log] handleClick: 事务已分发 (Transaction dispatched)', 'color: orange;');

                    // We've handled the click, stop searching.
                    return true;
                }
            }

            return false;
          },
          decorations: state => {

            const { doc } = state
            const decorations = []

            // 1. First, get a map of all headings
            const headings = findHeadings(doc)
            if (headings.length > 0) {
              console.log('[Log] decorations: 找到的所有标题:', headings.map(h => ({ 
                level: h.level, 
                pos: h.pos, 
                collapsed: h.node.attrs.collapsed,
                text: h.node.textContent.substring(0, 20) + '...'
              })))
            } else {
              console.log('[Log] decorations: 未找到任何标题')
              return null
            }

            headings.forEach((headingInfo, index) => {
              const { node, pos, level } = headingInfo

              // 2. If this heading is not collapsed, ignore it
              if (!node.attrs.collapsed) {
                
                return // Only process collapsed headings
              }
              
              console.log(`%c[Log] decorations: 处理折叠的标题 at pos ${pos}, level ${level}, 文本: "${node.textContent.substring(0, 30)}..."`, 'color: blue; font-weight: bold;')

              const from = pos + node.nodeSize
              let to = doc.content.size // By default, hide until the end
              
              console.log(`[Log] decorations: 初始范围 from: ${from}, to: ${to} (文档结尾)`)

              // 3. Find the next heading of the same or higher level
              let boundaryFound = false
              let nextHeadingInfo = null
              
              for (let i = index + 1; i < headings.length; i++) {
                const nextHeading = headings[i]
                console.log(`[Log] decorations:  -> 检查下一个标题 at pos ${nextHeading.pos}, level ${nextHeading.node.attrs.level}, 文本: "${nextHeading.node.textContent.substring(0, 20)}..."`)
                
                if (nextHeading.node.attrs.level <= level) {
                  to = nextHeading.pos
                  boundaryFound = true
                  nextHeadingInfo = nextHeading
                  console.log(`[Log] decorations:  ==> 找到边界! 下一个同级/更高级标题 at pos ${nextHeading.pos}, level ${nextHeading.node.attrs.level}. 停止搜索.`)
                  break // Found the boundary, stop searching
                } else {
                  console.log(`[Log] decorations:  -> 忽略. 下一个标题 level ${nextHeading.node.attrs.level} > 当前 level ${level}`)
                }
              }
              
              if (!boundaryFound) {
                  console.log(`[Log] decorations: 未找到后续边界，将隐藏到文档末尾.`)
              }

              // 4. Create a NODE decoration for each block node in the calculated range
              if (from < to) {
                let hiddenNodesCount = 0
                doc.nodesBetween(from, to, (node, pos, parent) => {
                  // We only want to hide direct children of the document.
                  // This prevents hiding nested list items individually.
                  if (parent.type.name === 'doc') {
                    console.log(`%c[Log] decorations: 应用节点装饰 at pos ${pos} for node ${node.type.name}`, 'color: purple;');
                    decorations.push(
                      Decoration.node(pos, pos + node.nodeSize, { class: 'hidden-content' })
                    )
                    hiddenNodesCount++
                  }
                });
                console.log(`[Log] decorations: 为标题 "${headingInfo.node.textContent.substring(0, 20)}..." 隐藏了 ${hiddenNodesCount} 个节点，范围: ${from}-${to}`)
              } else {
                console.log(`%c[Log] decorations: 范围无效 (from >= to)，不应用装饰. from: ${from}, to: ${to}`, 'color: red;')
              }
            })

            if (decorations.length > 0) {
              console.log('[Log] decorations: 创建并返回 DecorationSet，包含的装饰:', decorations.length, '个装饰');
              return DecorationSet.create(doc, decorations)
            }
            
            console.log('[Log] decorations: 没有活动的装饰，返回 null')
            return null
          }
        }
      })
    ]
  }
}